import type { FetchApiClient } from './fetch-api-client'
import type {
  ICreateViewStateRequest,
  ICreateViewTypeRequest,
  IDeleteViewStateRequest,
  IDeleteViewTypeRequest,
  IGetDefaultViewRequest,
  IGetViewStateRequest,
  IGetViewTypeRequest,
  IListViewStatesRequest,
  IListViewStatesResponse,
  IListViewTypesRequest,
  IListViewTypesResponse,
  IUpdateViewStateRequest,
  IUpdateViewTypeRequest,
  IViewState,
  IViewType,
} from '../types'

const VIEW_STATES_PATH = '/v1/view-states'
const VIEW_TYPES_PATH = '/v1/view-types'

const VISIBILITY_MAP: Record<string, number> = {
  VISIBILITY_UNSPECIFIED: 0,
  SYSTEM: 1,
  TENANT: 2,
  USER: 3,
}

function normalizeVisibility<T extends { visibility?: unknown }>(item: T): T {
  if (typeof item.visibility === 'string') {
    return {
      ...item,
      visibility: VISIBILITY_MAP[item.visibility] ?? -1,
    }
  }
  return item
}

export class ViewStateApiClientV1 {
  private readonly restClient: FetchApiClient

  constructor(restClient: FetchApiClient) {
    this.restClient = restClient
  }

  private buildQueryParams(
    params: Record<string, unknown>,
  ): Record<string, string> {
    const result: Record<string, string> = {}
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        result[key] = String(value)
      }
    }
    return result
  }

  async getViewState(request: IGetViewStateRequest): Promise<IViewState> {
    const result = await this.restClient.get<IViewState>(
      `${VIEW_STATES_PATH}/${request.id}`,
    )
    return normalizeVisibility(result)
  }

  async createViewState(request: ICreateViewStateRequest): Promise<IViewState> {
    const result = await this.restClient.post<IViewState>(VIEW_STATES_PATH, {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    return normalizeVisibility(result)
  }

  async updateViewState(request: IUpdateViewStateRequest): Promise<IViewState> {
    if (!request.viewState?.id) {
      throw new Error('updateViewState requires viewState.id')
    }
    const result = await this.restClient.put<IViewState>(
      `${VIEW_STATES_PATH}/${request.viewState.id}`,
      {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    )
    return normalizeVisibility(result)
  }

  async deleteViewState(request: IDeleteViewStateRequest): Promise<void> {
    const response = await this.restClient.fetch(
      `${VIEW_STATES_PATH}/${request.id}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const body = await response.text().catch(() => '')
      throw new Error(
        `DELETE ${VIEW_STATES_PATH}/${request.id} failed: ${response.status} ${body}`,
      )
    }
  }

  async getDefaultViewState(
    request: IGetDefaultViewRequest,
  ): Promise<IViewState> {
    const queryParams = this.buildQueryParams({
      typeId: request.typeId,
      scope: request.scope,
    })
    const result = await this.restClient.get<IViewState>(
      `${VIEW_STATES_PATH}/default`,
      {},
      { queryParams },
    )
    return normalizeVisibility(result)
  }

  async listViewStates(
    request: IListViewStatesRequest,
  ): Promise<IListViewStatesResponse> {
    const queryParams = this.buildQueryParams({
      limit: request.limit,
      page: request.page,
      name: request.name,
      typeId: request.typeId,
      scope: request.scope,
    })
    const result = await this.restClient.get<IListViewStatesResponse>(
      VIEW_STATES_PATH,
      {},
      { queryParams },
    )
    return {
      ...result,
      viewStates: result.viewStates.map(normalizeVisibility),
    }
  }

  async getViewType(request: IGetViewTypeRequest): Promise<IViewType> {
    const result = await this.restClient.get<IViewType>(
      `${VIEW_TYPES_PATH}/${request.id}`,
    )
    return normalizeVisibility(result)
  }

  async createViewType(request: ICreateViewTypeRequest): Promise<IViewType> {
    const result = await this.restClient.post<IViewType>(VIEW_TYPES_PATH, {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    return normalizeVisibility(result)
  }

  async updateViewType(request: IUpdateViewTypeRequest): Promise<IViewType> {
    if (!request.viewType?.id) {
      throw new Error('updateViewType requires viewType.id')
    }
    const result = await this.restClient.put<IViewType>(
      `${VIEW_TYPES_PATH}/${request.viewType.id}`,
      {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    )
    return normalizeVisibility(result)
  }

  async deleteViewType(request: IDeleteViewTypeRequest): Promise<void> {
    const response = await this.restClient.fetch(
      `${VIEW_TYPES_PATH}/${request.id}`,
      { method: 'DELETE' },
    )
    if (!response.ok) {
      const body = await response.text().catch(() => '')
      throw new Error(
        `DELETE ${VIEW_TYPES_PATH}/${request.id} failed: ${response.status} ${body}`,
      )
    }
  }

  async listViewTypes(
    request: IListViewTypesRequest,
  ): Promise<IListViewTypesResponse> {
    const queryParams = this.buildQueryParams({
      limit: request.limit,
      page: request.page,
      name: request.name,
    })
    const result = await this.restClient.get<IListViewTypesResponse>(
      VIEW_TYPES_PATH,
      {},
      { queryParams },
    )
    return {
      ...result,
      viewTypes: result.viewTypes.map(normalizeVisibility),
    }
  }
}
